import { isNativeApp } from './native';

type PositionCallback = (latitude: number, longitude: number) => void;

let watchId: string | null = null;
let webWatchId: number | null = null;
/** Invalidates in-flight watchPosition startups so overlapping resume calls cannot leak GPS watches. */
let watchToken = 0;

async function clearWatches(): Promise<void> {
  if (watchId) {
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      await Geolocation.clearWatch({ id: watchId });
    } catch { /* ignore */ }
    watchId = null;
  }
  if (webWatchId !== null && navigator.geolocation) {
    navigator.geolocation.clearWatch(webWatchId);
    webWatchId = null;
  }
}

/** Start location watch — uses Capacitor on native, navigator.geolocation on web. */
export async function startLocationWatch(onPosition: PositionCallback): Promise<void> {
  const token = ++watchToken;
  await clearWatches();
  if (token !== watchToken) return;

  if (isNativeApp()) {
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const perm = await Geolocation.checkPermissions();
      if (perm.location === 'prompt') {
        await Geolocation.requestPermissions();
      }
      if (token !== watchToken) return;

      const id = await Geolocation.watchPosition(
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 5000 },
        (pos, err) => {
          if (token !== watchToken) return;
          if (err || !pos) return;
          onPosition(pos.coords.latitude, pos.coords.longitude);
        }
      );
      if (token !== watchToken) {
        try {
          await Geolocation.clearWatch({ id });
        } catch { /* ignore */ }
        return;
      }
      watchId = id;
      return;
    } catch { /* fall through to web API */ }
  }

  if (token !== watchToken) return;

  if (navigator.geolocation) {
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        if (token !== watchToken) return;
        onPosition(pos.coords.latitude, pos.coords.longitude);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 5000 }
    );
    if (token !== watchToken) {
      navigator.geolocation.clearWatch(id);
      return;
    }
    webWatchId = id;
  }
}

export async function stopLocationWatch(_sendDisconnect?: boolean): Promise<void> {
  watchToken += 1;
  await clearWatches();
}

/** Android foreground service — keeps on-duty notification while session active. */
export async function startForegroundService(unitName: string): Promise<void> {
  if (!isNativeApp()) return;

  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.getPlatform() !== 'android') return;

    const { ForegroundService } = await import('@capawesome-team/capacitor-android-foreground-service');

    await ForegroundService.createNotificationChannel({
      id: 'cadsys_mdt_duty',
      name: 'On duty',
      description: 'Shows when CADSys MDT shift is active',
      importance: 3,
    });

    await ForegroundService.startForegroundService({
      id: 1001,
      title: 'CADSys MDT — On duty',
      body: `Unit ${unitName} — tracking active until logout`,
      smallIcon: 'ic_launcher_foreground',
      notificationChannelId: 'cadsys_mdt_duty',
    });
  } catch (err) {
    console.warn('Foreground service unavailable:', err);
  }
}

export async function stopForegroundService(): Promise<void> {
  if (!isNativeApp()) return;

  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.getPlatform() !== 'android') return;

    const { ForegroundService } = await import('@capawesome-team/capacitor-android-foreground-service');
    await ForegroundService.stopForegroundService();
  } catch { /* ignore */ }
}
